class AdaptiveLevelService {
  constructor() {
    this.profiles = new Map();
    this.loadFromStorage();
  }

  static getInstance() {
    if (!AdaptiveLevelService.instance) {
      AdaptiveLevelService.instance = new AdaptiveLevelService();
    }
    return AdaptiveLevelService.instance;
  }

  // Initialise le profil d'un enfant
  initializeChildProfile(childId, age) {
    const baseLevel = this.getBaseLevelForAge(age);
    
    const profile = {
      childId,
      currentLevel: baseLevel,
      feedbackHistory: [],
      adaptationScore: 0,
      lastUpdated: new Date(),
      preferredComplexity: baseLevel,
    };

    this.profiles.set(childId, profile);
    this.saveToStorage();
    return profile;
  }

  // Niveau de base selon l'âge
  getBaseLevelForAge(age) {
    if (age <= 4) return 1; // Très simple
    if (age <= 6) return 2; // Simple
    if (age <= 9) return 3; // Moyen
    if (age <= 12) return 4; // Avancé
    return 5; // Complexe
  }

  // Enregistre un feedback et adapte le niveau
  async recordFeedback(feedback) {
    let profile = this.profiles.get(feedback.childId);
    
    if (!profile) {
      profile = this.initializeChildProfile(feedback.childId, feedback.childAge);
    }

    // Ajoute le feedback à l'historique
    profile.feedbackHistory.push(feedback);
    
    // Garde seulement les 20 derniers feedbacks pour l'analyse
    if (profile.feedbackHistory.length > 20) {
      profile.feedbackHistory = profile.feedbackHistory.slice(-20);
    }

    // Calcule le nouveau niveau selon le feedback
    this.adaptLevel(profile, feedback);
    
    profile.lastUpdated = new Date();
    this.profiles.set(feedback.childId, profile);
    
    await this.saveToStorage();
    
    console.log(`🎯 Niveau adapté pour enfant ${feedback.childId}: ${profile.currentLevel}`);
  }

  // Logique d'adaptation du niveau
  adaptLevel(profile, feedback) {
    const currentLevel = profile.currentLevel;
    let adjustment = 0;

    switch (feedback.feedback) {
      case 'too_hard':
        adjustment = -0.5; // Diminue le niveau
        profile.adaptationScore = Math.max(-2, profile.adaptationScore - 0.5);
        console.log(`📉 Niveau trop difficile pour enfant ${profile.childId}, ajustement: ${adjustment}`);
        break;

      case 'more_info':
        adjustment = +0.3; // Augmente légèrement le niveau
        profile.adaptationScore = Math.min(2, profile.adaptationScore + 0.3);
        console.log(`📈 Enfant ${profile.childId} demande plus d'infos, ajustement: ${adjustment}`);
        break;

      case 'understood':
        // Analyse les patterns récents
        const recentUnderstood = this.getRecentFeedbackCount(profile, 'understood', 5);
        if (recentUnderstood >= 3) {
          adjustment = +0.2; // Peut augmenter le niveau
          profile.adaptationScore = Math.min(2, profile.adaptationScore + 0.2);
          console.log(`🎯 Enfant ${profile.childId} comprend bien, ajustement: ${adjustment}`);
        }
        break;
    }

    // Applique l'ajustement avec limites
    const newLevel = Math.max(1, Math.min(5, currentLevel + adjustment));
    profile.currentLevel = Number(newLevel.toFixed(1));
    
    // Calcule le niveau préféré basé sur l'historique
    profile.preferredComplexity = this.calculatePreferredComplexity(profile);
  }

  // Compte les feedbacks récents d'un type
  getRecentFeedbackCount(profile, type, count) {
    return profile.feedbackHistory
      .slice(-count)
      .filter(f => f.feedback === type).length;
  }

  // Calcule le niveau de complexité préféré
  calculatePreferredComplexity(profile) {
    if (profile.feedbackHistory.length < 5) {
      return profile.currentLevel;
    }

    const recent = profile.feedbackHistory.slice(-10);
    const tooHardCount = recent.filter(f => f.feedback === 'too_hard').length;
    const moreInfoCount = recent.filter(f => f.feedback === 'more_info').length;

    // Algorithme de calcul du niveau préféré
    let preferred = profile.currentLevel;
    
    if (tooHardCount > moreInfoCount * 2) {
      preferred -= 0.5; // Préfère plus simple
    } else if (moreInfoCount > 3) {
      preferred += 0.3; // Peut gérer plus complexe
    }

    return Math.max(1, Math.min(5, preferred));
  }

  // Récupère le niveau actuel pour un enfant
  getCurrentLevel(childId) {
    const profile = this.profiles.get(childId);
    return profile ? profile.currentLevel : 3; // Niveau par défaut
  }

  // Génère les paramètres pour l'IA selon le niveau
  getAIParameters(childId, age) {
    const level = this.getCurrentLevel(childId);
    
    console.log(`🎯 Paramètres IA pour enfant ${childId}: niveau ${level}`);

    if (age <= 5) {
      return {
        complexityLevel: Math.min(2, level),
        vocabularyLevel: level <= 1.5 ? 'très_simple' : 'simple',
        explanationDepth: 'basique',
        exampleCount: level <= 1.5 ? 1 : 2,
      };
    } else if (age <= 8) {
      return {
        complexityLevel: Math.min(3, level),
        vocabularyLevel: level <= 2 ? 'simple' : level <= 3 ? 'moyen' : 'enrichi',
        explanationDepth: level <= 2 ? 'courte' : 'détaillée',
        exampleCount: Math.min(3, Math.floor(level) + 1),
      };
    } else if (age <= 12) {
      return {
        complexityLevel: level,
        vocabularyLevel: level <= 2 ? 'simple' : level <= 3.5 ? 'scolaire' : 'avancé',
        explanationDepth: level <= 3 ? 'standard' : 'approfondie',
        exampleCount: Math.min(4, Math.floor(level) + 1),
      };
    } else {
      return {
        complexityLevel: level,
        vocabularyLevel: level <= 3 ? 'scolaire' : 'avancé',
        explanationDepth: level <= 3.5 ? 'complète' : 'académique',
        exampleCount: Math.min(5, Math.floor(level) + 2),
      };
    }
  }

  // Stats pour les parents
  getChildStats(childId) {
    const profile = this.profiles.get(childId);
    if (!profile) {
      return {
        totalInteractions: 0,
        currentLevel: 3,
        learningTrend: 'stable',
        strongTopics: [],
        needsHelp: [],
      };
    }

    const recent = profile.feedbackHistory.slice(-10);
    const tooHardRatio = recent.length > 0 ? recent.filter(f => f.feedback === 'too_hard').length / recent.length : 0;
    
    let trend = 'stable';
    if (tooHardRatio > 0.4) {
      trend = 'struggling';
    } else if (profile.adaptationScore > 0.5) {
      trend = 'improving';
    }

    return {
      totalInteractions: profile.feedbackHistory.length,
      currentLevel: profile.currentLevel,
      learningTrend: trend,
      strongTopics: [],
      needsHelp: [],
    };
  }

  // Sauvegarde locale
  async saveToStorage() {
    try {
      const data = Array.from(this.profiles.entries());
      localStorage.setItem('adaptive_profiles', JSON.stringify(data));
    } catch (error) {
      console.error('Erreur sauvegarde profils adaptatifs:', error);
    }
  }

  // Chargement depuis le stockage local
  loadFromStorage() {
    try {
      const data = localStorage.getItem('adaptive_profiles');
      if (data) {
        const profiles = JSON.parse(data);
        this.profiles = new Map(profiles.map(([key, value]) => [
          key, 
          {
            ...value,
            lastUpdated: new Date(value.lastUpdated),
            feedbackHistory: value.feedbackHistory.map(f => ({
              ...f,
              timestamp: new Date(f.timestamp)
            }))
          }
        ]));
        console.log(`📚 ${this.profiles.size} profils adaptatifs chargés`);
      }
    } catch (error) {
      console.error('Erreur chargement profils adaptatifs:', error);
    }
  }
}

export default AdaptiveLevelService;